"use client";

import RecentPassengers from "@/containers/components/recentPassengers/RecentPassengers";
import RecentPassengersMobile from "@/containers/components/recentPassengers/RecentPassengersMobile";
import data from "@/public/data/flight/passengers.json";
import { Button } from "@/shared/components/common/button";
import Flex from "@/shared/components/common/Flex";
import Separator from "@/shared/components/common/separator";
import Typography from "@/shared/components/common/Typography";
import { useDeviceType } from "@/shared/context/DeviceContext";
import useAuthToken from "@/shared/hooks/useAuthToken";
import type { PassengersSchemaType } from "@/shared/schemas/flight/passengersSchema";
import { Trash, User } from "@phosphor-icons/react/dist/ssr";
import { format as gFormat } from "date-fns";
import { format } from "date-fns-jalali";
import { useFormContext } from "react-hook-form";

interface PassengerCardButtonsProps {
  recentPassengers: RecentPassenger[];
  changeToggle: (value: boolean) => void;
  type: "adult" | "child" | "infant";
  passengerIndex: number;
}

export default function PassengerCardButtons({
  changeToggle,
  recentPassengers,
  type,
  passengerIndex,
}: PassengerCardButtonsProps) {
  const {
    forms: {
      passengersForm: { removePassenger, recentPassegners, passengerType },
    },
  } = data;

  const onSelectPassenger = ({
    firstname,
    lastname,
    identifier,
    nationality,
    sexuality,
    birthdate,
  }: RecentPassenger) => {
    const [year, month, day] = birthdate
      ? (getValues(`passengers.${passengerIndex}.isInternational`) ||
          getValues(`passengers.${passengerIndex}.isForeign`)
          ? gFormat
          : format)(birthdate ?? "", "yyyy-M-d").split("-")
      : "";

    setValue(`passengers.${passengerIndex}`, {
      ...getValues(`passengers.${passengerIndex}`),
      en_first_name: firstname,
      en_last_name: lastname,
      na_code:
        getValues(`passengers.${passengerIndex}.isInternational`) ||
        getValues(`passengers.${passengerIndex}.isForeign`)
          ? ""
          : identifier,
      gender: sexuality,
      nationality:
        getValues(`passengers.${passengerIndex}.isInternational`) ||
        getValues(`passengers.${passengerIndex}.isForeign`)
          ? nationality.name
          : "IRN",
      birth_date: {
        day,
        month,
        year,
      },
    });
  };

  const { setValue, getValues } = useFormContext<PassengersSchemaType>();

  const isLogin = Boolean(useAuthToken());
  const userDeviceType = useDeviceType();

  return (
    <Flex className="max-md:flex-col gap-y-4 justify-end md:justify-between items-start w-full">
      <Flex className="bg-muted dark:bg-muted/10 border border-border py-1 px-3 rounded-md">
        <Typography
          variant="h6"
          className="font-medium text-muted-foreground text-sm"
        >
          {passengerType.find(({ value }) => type === value)?.title ?? ""}
        </Typography>
      </Flex>
      <Separator direction="horizontal" className="md:hidden !bg-border" />
      <Flex className="max-md:w-full items-center justify-start max-md:justify-end gap-x-2">
        {isLogin ? (
          userDeviceType === "mobile" ? (
            <RecentPassengersMobile
              passengers={recentPassengers}
              onSelect={onSelectPassenger}
            >
              <Button
                className="border-2 border-primary bg-transparent group"
                type="button"
              >
                <Flex className="items-center gap-x-2">
                  <User
                    className="w-4 h-4 text-primary group-hover:text-background"
                    weight="fill"
                  />
                  <Typography className="font-normal text-primary group-hover:text-background">
                    {recentPassegners.trigger}
                  </Typography>
                </Flex>
              </Button>
            </RecentPassengersMobile>
          ) : (
            <RecentPassengers
              passengers={recentPassengers}
              onSelect={onSelectPassenger}
            >
              <Button
                className="border-2 border-primary bg-transparent group"
                type="button"
              >
                <Flex className="items-center gap-x-2">
                  <User
                    className="w-4 h-4 text-primary group-hover:text-background"
                    weight="fill"
                  />
                  <Typography className="font-normal text-primary group-hover:text-background">
                    {recentPassegners.trigger}
                  </Typography>
                </Flex>
              </Button>
            </RecentPassengers>
          )
        ) : (
          <Button
            className="border-2 border-primary bg-transparent group"
            type="button"
            onClick={() => changeToggle(true)}
          >
            <Flex className="items-center gap-x-2">
              <User
                className="w-4 h-4 text-primary group-hover:text-background"
                weight="fill"
              />
              <Typography className="font-normal text-primary group-hover:text-background">
                {recentPassegners.trigger}
              </Typography>
            </Flex>
          </Button>
        )}
        {passengerIndex !== 0 ? (
          <Button
            onClick={() => {
              const passengers = getValues("passengers");
              setValue("passengers", [
                ...passengers.slice(0, passengerIndex),
                ...passengers.slice(passengerIndex + 1),
              ]);
            }}
            variant="destructive"
            className="hover:bg-background! border-2 border-destructive group transition duration-75 ease-ease-in-out-circ !px-1"
          >
            <Flex className="items-center justify-between gap-x-1">
              <Typography className="font-normal group-hover:text-destructive transition duration-75 ease-ease-in-out-circ">
                {removePassenger}
              </Typography>
              <Trash className="w-5 h-5 text-background group-hover:text-destructive transition duration-75 ease-ease-in-out-circ" />
            </Flex>
          </Button>
        ) : (
          <></>
        )}
      </Flex>
    </Flex>
  );
}
